import { CertificateFormData } from "../types/certificate";
import { getSensorSchemaFromCategoryAndType } from "../sensors/sensorSchemas";
import {
  buildCertificateDisplayContext,
  formatAtmPressureForCertificateForm,
  type CertificateMeasurementUnitSource,
} from "./certificateFormContext";

/** Sensors that support test certificate generate/download in the UI. */
export function isCertificateSupportedSensor(
  sensorCategory?: string | null,
  sensorType?: string | null
): boolean {
  const categoryLower = sensorCategory?.toLowerCase() || "";
  const typeLower = sensorType?.toLowerCase() || "";
  const isPiezometer =
    categoryLower.includes("piezometer") || typeLower.includes("piezometer");
  const isVibratingWire =
    typeLower === "vibrating-wire" ||
    typeLower === "vibrating_wire" ||
    typeLower.includes("vibrating");
  const isAccelerometer = categoryLower.includes("accelerometer");
  const isTiltmeter = categoryLower.includes("tiltmeter");
  const isResistiveStrainGauge = categoryLower.includes("resistive strain gauge");
  return isPiezometer || isVibratingWire || isAccelerometer || isTiltmeter || isResistiveStrainGauge;
}

export function isAccelerometerSensor(sensorCategory?: string | null): boolean {
  return (sensorCategory?.toLowerCase() || "").includes("accelerometer");
}

export function isTiltmeterSensor(sensorCategory?: string | null): boolean {
  return (sensorCategory?.toLowerCase() || "").includes("tiltmeter");
}

export function isResistiveStrainGaugeSensor(sensorCategory?: string | null): boolean {
  return (sensorCategory?.toLowerCase() || "").includes("resistive strain gauge");
}

/** Whether the list/result page should show the certificate action for this calibration. */
export function canShowCertificateAction(
  sensorCategory: string | null | undefined,
  calibrationStatus?: string | null,
  hasResult?: boolean
): boolean {
  if (!hasResult) return false;
  const status = calibrationStatus?.toLowerCase().trim() || "";
  return status === "passed";
}

function pickNumber(...values: unknown[]): number | undefined {
  for (const v of values) {
    if (v === undefined || v === null || v === "") continue;
    const n = typeof v === "number" ? v : parseFloat(String(v));
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

export function toYYYYMMDD(dateSource?: string | null): string {
  if (!dateSource) return "";
  const normalized = dateSource.trim();
  const utilityDateMatch = normalized.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (utilityDateMatch) {
    const [, dd, mm, yyyy] = utilityDateMatch;
    return `${yyyy}-${mm}-${dd}`;
  }
  const d = new Date(dateSource);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Build certificate form defaults from calibration + result.
 * Returns null if data is incomplete or sensor type is unsupported.
 */
export function buildCertificateFormData(
  sensorCategory: string | null | undefined,
  sensorType: string | null | undefined,
  calibration: { input_data?: Record<string, unknown>; unit?: string | null },
  result: {
    calibration_date?: string;
    created_date?: string;
    output_data?: Record<string, unknown>;
  },
  unitContext?: CertificateMeasurementUnitSource
): CertificateFormData | null {
  if (!isCertificateSupportedSensor(sensorCategory, sensorType)) {
    return null;
  }

  const inputData = calibration?.input_data || {};
  const outputData = (result?.output_data || {}) as Record<string, unknown>;
  if (!inputData || !outputData) return null;

  const dateSource = result.calibration_date || result.created_date;
  const formattedDate =
    toYYYYMMDD(dateSource) ||
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`;

  if (
    isAccelerometerSensor(sensorCategory) ||
    isTiltmeterSensor(sensorCategory) ||
    isResistiveStrainGaugeSensor(sensorCategory)
  ) {
    const summary = (outputData.summary || {}) as Record<string, unknown>;
    const testReport = (inputData.test_report || {}) as Record<string, unknown>;
    const reportDate = testReport.calibration_date ? String(testReport.calibration_date) : null;
    const reportedDate =
      toYYYYMMDD(reportDate) ||
      formattedDate;
    // Prefer Daily Environmental Data values stored on the calibration
    // (ambient_temperature / humidity / temperature), then utility/test_report.
    const tempValue = pickNumber(
      inputData.ambient_temperature,
      inputData.temperature,
      testReport.temperature,
      summary.temperature
    );
    const temperature =
      tempValue !== undefined && tempValue !== null
        ? `${Math.round(tempValue)} °C`
        : "26 °C";
    const humidityValue = pickNumber(
      inputData.humidity,
      testReport.humidity,
      summary.humidity
    );
    const humidity =
      humidityValue !== undefined && humidityValue !== null
        ? `${Math.round(humidityValue)} %`
        : "";
    return {
      date: reportedDate,
      temperature,
      humidity,
    };
  }

  const summary = (outputData.summary || {}) as Record<string, unknown>;
  // ambient_temperature (from Daily Environmental Data, injected server-side)
  // must win over temperature_frequency[0], which is the pasted Room T
  // calibration reading — a different value that happens to almost always
  // be present, so it was silently masking the real ambient reading.
  const tempValue = pickNumber(
    inputData.ambient_temperature,
    summary.ambient_temperature,
    outputData.ambient_temperature,
    outputData.ambientTemp,
    summary.temperature,
    summary.temp,
    outputData.temperature,
    outputData.temp,
    (inputData as any).temperature_frequency?.[0]?.temperature
  );
  const temperature =
    tempValue !== undefined && tempValue !== null
      ? `${Math.round(tempValue)} °C`
      : "26 °C";

  const atmPressureValue = pickNumber(
    inputData.atmospheric_pressure,
    summary.atmospheric_pressure,
    outputData.atmospheric_pressure,
    outputData.atm_pressure
  );
  const certSchema = getSensorSchemaFromCategoryAndType(sensorCategory, sensorType);
  const certCtx = buildCertificateDisplayContext({
    ...unitContext,
    calibration,
    schemaDefaultUnit: certSchema?.unit,
  });
  const atmPressure = formatAtmPressureForCertificateForm(
    atmPressureValue,
    certCtx.measurementUnit
  );

  const cableLengthValue = inputData.cable_length;
  const cableLength =
    cableLengthValue !== undefined && cableLengthValue !== null
      ? `${cableLengthValue} Meter`
      : undefined;

  const dwtNoRaw =
    inputData.dwt_no ??
    inputData.dwtTraceable ??
    inputData.dwtCertificate ??
    inputData.dwt_certificate ??
    inputData.equipment_calibration_certificate_no ??
    summary.dwt_traceable_certificate_no ??
    summary.dwt_certificate ??
    summary.dwtTraceable ??
    summary.equipment_calibration_certificate_no ??
    outputData.equipment_calibration_certificate_no ??
    outputData.equipmentCalibrationCertificateNo ??
    outputData.dwt_traceable_certificate_no ??
    outputData.dwt_certificate ??
    outputData.dwtTraceable;
  const dwtNo =
    dwtNoRaw !== undefined && dwtNoRaw !== null && dwtNoRaw !== ""
      ? String(dwtNoRaw)
      : undefined;

  return {
    date: formattedDate,
    temperature,
    atmPressure,
    cableLength,
    dwtNo,
  };
}
